import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, Clock, Shield, Sparkles, Award, Globe2, Star, Check, Plane, Waves } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import BookingForm from "../components/BookingForm";
import SEO from "../components/SEO";
import Testimonials from "../components/Testimonials";

const SUV_IMG = "https://images.unsplash.com/photo-1758217209786-95458c5d30a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHx3aGl0ZSUyMGx1eHVyeSUyMFNVViUyMGRyaXZpbmd8ZW58MHx8fHwxNzc5MTQ5MzI3fDA&ixlib=rb-4.1.0&q=85";
const AIRPORT_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_brothers-taxi-web/artifacts/gmifdzt1_brothers%20taxi%20id4%20tirana%20airport.png";
const RIVIERA_IMG = "/riviera.png";

const FLEET_CAR_IMG = {
  id4: "https://customer-assets-4nw71qhi.emergentagent.net/job_brothers-taxi-web/artifacts/qljglrgo_brothers%20taxi%20id4%20tirana%20airport.png",
  byd: "https://customer-assets-4nw71qhi.emergentagent.net/job_brothers-taxi-web/artifacts/q718vh2m_byd%20sarande%20brotherstaxi.png",
  gla: "https://customer-assets-4nw71qhi.emergentagent.net/job_brothers-taxi-web/artifacts/9ayxnyuq_brotherstaxi%20gla%20albania.png",
};

const Home = () => {
  const { t } = useLang();
  const reasonsIcons = [Clock, Sparkles, Award, Shield, MapPin, Globe2];

  return (
    <>
      <SEO
        title={t.seo.home.title}
        description={t.seo.home.description}
        keywords={t.seo.home.keywords}
        path="/"
      />

      {/* ============ HERO ============ */}
      <section className="relative pt-28 md:pt-32 pb-16 md:pb-24 bg-paper overflow-hidden">
        {/* decorative shapes */}
        <div className="absolute top-20 left-0 w-72 h-72 bg-[#FFD500] rounded-full blur-[120px] opacity-30 pointer-events-none" />
        <div className="absolute top-10 right-0 w-40 h-40 checkered-fine opacity-20 rotate-12 pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 anim-fade-up">
            <div className="bt-tag mb-7">
              <span className="h-2 w-2 rounded-full bg-[#FFD500] animate-pulse" />
              {t.hero.tagline}
            </div>

            <h1 className="font-display text-[44px] sm:text-6xl lg:text-7xl xl:text-[88px] font-black uppercase tracking-tighter leading-[0.92] text-zinc-900">
              {t.hero.title}
              <br />
              <span className="font-serif italic font-normal text-zinc-700 normal-case tracking-tight">
                {t.hero.subtitle}
              </span>
            </h1>

            <p className="mt-7 text-zinc-600 text-lg leading-relaxed max-w-xl">{t.hero.description}</p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/book" data-testid="hero-book-cta" className="bt-btn-primary text-base px-8 py-5">
                {t.hero.ctaPrimary}
                <ArrowRight className="h-5 w-5" />
              </Link>
              <a href="tel:+355682222286" data-testid="hero-call-cta" className="bt-btn-outline">
                <Phone className="h-4 w-4" />
                +355 68 22 22 286
              </a>
            </div>

            {/* Trust row */}
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1.5">
                  {[1,2,3,4,5].map((s) => (
                    <Star key={s} className="h-4 w-4 fill-[#FFD500] text-[#FFD500]" />
                  ))}
                </div>
                <span className="font-bold text-zinc-900">4.9</span>
                <span className="text-zinc-500">· 237 {t.common.customers}</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-700 font-semibold">
                <Check className="h-4 w-4 text-[#FFD500]" strokeWidth={3} />
                {t.common.fixedPrices}
              </div>
              <div className="flex items-center gap-2 text-zinc-700 font-semibold">
                <Check className="h-4 w-4 text-[#FFD500]" strokeWidth={3} />
                {t.common.noHiddenFees}
              </div>
            </div>

            {/* Stats */}
            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5 max-w-2xl pt-10 border-t border-zinc-200">
              {[
                { num: "10+", label: t.stats.years },
                { num: "25k+", label: t.stats.trips },
                { num: "20+", label: t.stats.cities },
                { num: "24/7", label: t.stats.support },
              ].map((s, i) => (
                <div key={i}>
                  <div className="font-display text-3xl md:text-4xl font-black text-zinc-900 tracking-tight">{s.num}</div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mt-1 font-bold">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Booking widget */}
          <div className="lg:col-span-5 anim-fade-up anim-delay-2">
            <div className="relative">
              <div className="absolute -top-3 -right-3 h-20 w-20 checkered-fine opacity-50" />
              <div className="relative bg-white border border-zinc-200 rounded-3xl overflow-hidden shadow-taxi-lift">
                <div className="bg-zinc-900 text-white px-6 py-5 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] font-extrabold text-[#FFD500] mb-1">{t.booking.title}</div>
                    <div className="text-zinc-300 text-sm">{t.booking.subtitle}</div>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-[#FFD500] flex items-center justify-center flex-shrink-0">
                    <ArrowRight className="h-5 w-5 text-zinc-900" />
                  </div>
                </div>
                <BookingForm compact />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ MARQUEE ============ */}
      <div className="relative bg-zinc-900 overflow-hidden">
        <div className="flex animate-taxi-marquee whitespace-nowrap py-5">
          {Array.from({ length: 2 }).flatMap((_, dup) =>
            ["Lushnjë → Tiranë", "Aeroport Rinas", "Sarandë", "Ksamil", "Dhërmi", "Himarë", "Durrës", "Vlorë", "Berat", "Korçë", "Shkodër"].map((dest, i) => (
              <div key={`${dup}-${i}`} className="flex items-center gap-4 px-8">
                <span className="font-display text-2xl md:text-3xl font-black uppercase text-white tracking-tight">{dest}</span>
                <span className="text-[#FFD500] text-3xl">★</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ============ WHY US ============ */}
      <section className="relative py-24 md:py-32 bg-mist">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="mb-14 max-w-3xl">
            <div className="bt-tag-yellow mb-5">{t.home.whySubtitle}</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black uppercase leading-[0.96] text-zinc-900 tracking-tighter">
              {t.home.whyTitle.split(" ").slice(0, -2).join(" ")} <span className="hl-yellow">{t.home.whyTitle.split(" ").slice(-2).join(" ")}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.home.reasons.map((r, i) => {
              const Icon = reasonsIcons[i] || Shield;
              return (
                <div
                  key={i}
                  data-testid={`reason-card-${i}`}
                  className="bg-white border border-zinc-200 rounded-3xl p-7 shadow-taxi-card hover:shadow-taxi-hover hover:-translate-y-1.5 transition-all group"
                >
                  <div className="h-14 w-14 rounded-2xl bg-[#FFD500] flex items-center justify-center mb-5 group-hover:rotate-[-8deg] transition-transform">
                    <Icon className="h-7 w-7 text-zinc-900" strokeWidth={2.2} />
                  </div>
                  <h3 className="font-display text-xl font-black uppercase text-zinc-900 mb-2 tracking-tight">{r.title}</h3>
                  <p className="text-zinc-600 text-sm leading-relaxed">{r.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ DESTINATIONS BENTO ============ */}
      <section className="relative py-24 md:py-32 bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="mb-14 max-w-3xl">
            <div className="bt-tag mb-5">{t.home.destinationsSubtitle}</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black uppercase leading-[0.96] text-zinc-900 tracking-tighter">
              <span className="font-serif italic font-normal text-zinc-600 normal-case tracking-tight block mb-2">{t.accents.whereNext}</span>
              {t.home.destinationsTitle}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {/* Airport - big */}
            <Link to="/airport-transfers" data-testid="dest-airport-card"
              className="md:col-span-7 group relative overflow-hidden h-[440px] rounded-3xl border border-zinc-200 shadow-taxi-card hover:shadow-taxi-lift transition-all"
            >
              <img src={AIRPORT_IMG} alt="Airport Transfer Tirana" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <div className="absolute top-5 left-5 bt-tag-yellow"><Plane className="h-3.5 w-3.5" /> {t.accents.airportTag}</div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h3 className="font-display text-3xl md:text-5xl font-black uppercase text-white tracking-tighter mb-3 leading-none">
                  {t.nav.airport}<br /><span className="text-[#FFD500]">Tiranë</span>
                </h3>
                <p className="text-zinc-200 text-sm max-w-md mb-4">{t.services.items[1].desc}</p>
                <div className="inline-flex items-center gap-2 bg-white text-zinc-900 px-5 py-2.5 rounded-full text-xs uppercase tracking-widest font-extrabold group-hover:bg-[#FFD500] transition-colors">
                  3500 L · {t.common.learnMore}
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Coast */}
            <Link to="/services" data-testid="dest-coast-card"
              className="md:col-span-5 group relative overflow-hidden h-[440px] rounded-3xl border border-zinc-200 shadow-taxi-card hover:shadow-taxi-lift transition-all"
            >
              <img src={RIVIERA_IMG} alt="Albanian Riviera" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
              <div className="absolute top-5 left-5 bt-tag-yellow"><Waves className="h-3.5 w-3.5" /> {t.accents.coastTag}</div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h3 className="font-display text-3xl md:text-5xl font-black uppercase text-white tracking-tighter mb-3 leading-none">
                  Ksamil<br />Sarandë<br /><span className="text-[#FFD500]">Dhërmi</span>
                </h3>
                <div className="inline-flex items-center gap-2 bg-white text-zinc-900 px-5 py-2.5 rounded-full text-xs uppercase tracking-widest font-extrabold group-hover:bg-[#FFD500] transition-colors">
                  {t.common.startingFrom} 7000 L
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Three small cards */}
            {[
              { name: "Tiranë", price: "4,000 L", duration: "60 min" },
              { name: "Vlorë", price: "5,000 L", duration: "90 min" },
              { name: "Sarandë", price: "9,000 L", duration: "180 min" },
            ].map((c, i) => (
              <Link key={i} to="/pricing" data-testid={`dest-small-card-${i}`}
                className="md:col-span-4 group relative overflow-hidden h-[200px] rounded-3xl border border-zinc-200 bg-white p-6 hover:bg-[#FFD500] shadow-taxi-card hover:-translate-y-1 transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="text-xs uppercase tracking-widest font-extrabold text-zinc-500">Lushnjë →</div>
                  <div className="font-display text-3xl md:text-4xl font-black uppercase text-zinc-900 tracking-tighter mt-1">{c.name}</div>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="font-display text-2xl font-black text-zinc-900">{c.price}</div>
                    <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">{c.duration} · Sedan</div>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-zinc-900 group-hover:bg-white flex items-center justify-center transition-colors">
                    <ArrowRight className="h-5 w-5 text-[#FFD500] group-hover:text-zinc-900" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FLEET PREVIEW - DARK ============ */}
      <section className="bg-zinc-900 text-white py-20 md:py-28 relative overflow-hidden" data-testid="fleet-preview-section">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#FFD500] blur-[160px] opacity-20 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#FFD500] mb-4">
                <span className="h-px w-8 bg-[#FFD500]" />
                {t.fleetPreview.tag}
              </div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-2xl">
                {t.fleetPreview.title}
              </h2>
              <p className="mt-5 text-zinc-400 text-base md:text-lg max-w-2xl leading-relaxed">{t.fleetPreview.subtitle}</p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/fleet"
                data-testid="fleet-preview-view-all"
                className="inline-flex items-center gap-2 text-white font-medium border-b border-white/40 hover:border-[#FFD500] hover:text-[#FFD500] pb-1 transition-colors"
              >
                {t.fleetPreview.viewAll} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {t.fleetPreview.cars.map((c, i) => (
              <Link
                key={i}
                to="/book"
                data-testid={`fleet-preview-card-${c.type}`}
                className="group bg-zinc-800/60 hover:bg-zinc-800 border border-zinc-700/60 rounded-3xl overflow-hidden flex flex-col transition-all hover:-translate-y-1"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-zinc-700">
                  <img
                    src={FLEET_CAR_IMG[c.type]}
                    alt={`${c.name} Brothers Taxi`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00B67A] text-white text-[10px] uppercase tracking-widest font-semibold">
                      {c.badge}
                    </span>
                  </div>
                </div>

                <div className="p-6 md:p-7 flex flex-col flex-1">
                  <h3 className="font-display text-xl md:text-2xl font-semibold text-white tracking-tight">{c.name}</h3>
                  <p className="mt-3 text-zinc-400 text-sm leading-relaxed">{c.desc}</p>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {c.specs.map((s, si) => (
                      <span
                        key={si}
                        className="inline-flex items-center px-2.5 py-1 rounded-full bg-zinc-700/60 text-zinc-200 text-[11px] font-medium"
                      >
                        {s}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 pt-5 border-t border-zinc-700/60 flex items-center justify-between text-sm">
                    <span className="text-zinc-500">{c.footer}</span>
                    <span className="font-semibold text-[#FFD500] inline-flex items-center gap-1.5 group-hover:gap-2 transition-all">
                      {t.fleetPreview.reserve} <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/fleet"
              data-testid="fleet-preview-cta"
              className="inline-flex items-center gap-2 bg-white text-zinc-900 font-semibold rounded-full px-7 py-3.5 text-sm hover:bg-[#FFD500] transition-colors"
            >
              {t.fleetPreview.viewAll}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ WHY BROTHERS TAXI ============ */}
      <section className="bg-white py-20 md:py-28 overflow-hidden" data-testid="why-brothers-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* LEFT: title + subtitle + photo below */}
          <div className="lg:col-span-6 flex flex-col">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-zinc-500 mb-5">
              <span className="h-px w-8 bg-zinc-400" />
              {t.whyBrothers.tag}
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 leading-[1.05] max-w-md">
              {t.whyBrothers.title}
            </h2>
            <p className="mt-5 text-zinc-600 text-base md:text-lg max-w-md leading-relaxed">
              {t.whyBrothers.subtitle}
            </p>

            {/* Photo below title */}
            <div className="mt-10 relative overflow-hidden rounded-[24px] md:rounded-[32px] border border-zinc-200 shadow-taxi-lift">
              <img
                src="https://customer-assets-4nw71qhi.emergentagent.net/job_brothers-taxi-web/artifacts/9ayxnyuq_brotherstaxi%20gla%20albania.png"
                alt="Brothers Taxi - Mercedes-Benz GLA në Aeroportin Ndërkombëtar të Tiranës (Rinas)"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
          </div>

          {/* RIGHT: numbered list */}
          <div className="lg:col-span-6 lg:pl-8">
            <ol className="space-y-0 divide-y divide-zinc-200 border-y border-zinc-200">
              {t.whyBrothers.items.map((item, i) => (
                <li
                  key={i}
                  data-testid={`why-item-${i}`}
                  className="grid grid-cols-12 gap-4 md:gap-8 py-6 md:py-8 items-start group hover:bg-white/60 transition-colors -mx-4 md:-mx-6 px-4 md:px-6"
                >
                  <div className="col-span-2 md:col-span-1 font-display text-sm md:text-base font-semibold text-zinc-400 pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-10 md:col-span-11">
                    <h3 className="font-display text-lg md:text-xl lg:text-2xl font-semibold text-zinc-900 tracking-tight">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-zinc-600 text-sm md:text-base leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <Testimonials />

      {/* ============ BIG YELLOW CTA ============ */}
      <section className="relative py-20 md:py-28 yellow-noise overflow-hidden">
        <div className="absolute -right-10 -top-10 w-72 h-72 rounded-full bg-zinc-900 opacity-5 blur-3xl" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-zinc-800 mb-4">
              <span className="h-px w-8 bg-zinc-800" />
              {t.home.ctaTag}
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] text-zinc-900">
              {t.home.ctaBlockTitle}
            </h2>
            <p className="text-zinc-800 mt-5 text-base md:text-lg max-w-2xl leading-relaxed">{t.home.ctaBlockSubtitle}</p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link to="/book" data-testid="cta-block-book" className="bt-btn-secondary w-full text-base py-5">
              {t.common.bookTaxi}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="tel:+355682222286" data-testid="cta-block-call" className="inline-flex items-center justify-center gap-2 bg-white text-zinc-900 border-2 border-zinc-900 font-semibold hover:bg-zinc-900 hover:text-white transition-colors rounded-full px-7 py-5 text-base">
              <Phone className="h-4 w-4" /> +355 68 22 22 286
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
